import type { ComponentType } from 'svelte';
import { CONTENT_TYPES } from '$lib/constants/constants';
import NewsletterSubscription from '../components/website/newsletter-subscription/NewsletterSubscription.svelte';
import PicturePanel from '$lib/components/website/PicturePanel.svelte';
import HeroBanner from '$lib/components/website/HeroBanner.svelte';
import RichTextPanel from '$lib/components/website/RichTextPanel.svelte';
import CtaPanel from '$lib/components/website/ctaPanel/CtaPanel.svelte';
import EquipmentPanel from '$lib/components/website/EquipmentPanel.svelte';
import EmbedPanel from '$lib/components/website/EmbedPanel.svelte';
import AccordionPanel from '$lib/components/website/accordion/AccordionPanel.svelte';
import UspPanel from '$lib/components/website/UspPanel.svelte';
import ReviewPanel from '$lib/components/website/review/ReviewPanel.svelte';
import DoubleWebColumn from '$lib/components/website/DoubleWebColumn.svelte';
import WebColumn from '$lib/components/website/WebColumn.svelte';
import CardPanel from '$lib/components/website/CardPanel.svelte';
import ExercisePanel from '$lib/components/website/ExercisePanel.svelte';
import RestPanel from '$lib/components/website/RestPanel.svelte';
import WorkoutPanel from '$lib/components/website/WorkoutPanel.svelte';
import PartnerPanel from '$lib/components/website/PartnerPanel.svelte';
import VariationContainer from '$lib/components/website/VariationContainer.svelte';
import HeroBannerCarousel from '$lib/components/website/HeroBannerCarousel.svelte';

type PanelData = {
  contentType: string;
  [key: string]: any;
};

const container = {
  homePage: 'px-4 lg:px-8 lg:max-w-1280',
  contentPage: 'px-4 lg:px-8 lg:max-w-1280',
  articlePage: 'w-full px-4 max-w-800 lg:px-0',
  trainerPage: 'px-4 lg:px-8 lg:max-w-1280',
};

// Several of the page and component types have different vertical white spacing
// This method just enables us to specify exact styles for each page
export const getUniqueContainerStyles = (pageType, componentType, index: number | null = null) => {
  if (componentType === CONTENT_TYPES.ACCORDION && pageType === 'contentPage')
    return 'px-4 lg:px-8 lg:max-w-1280 py-16 lg:gap-16 mt-8 lg:mt-16';

  if (componentType === CONTENT_TYPES.ACCORDION && pageType === 'articlePage')
    return 'w-full px-4 max-w-800 lg:px-0 py-16 lg:gap-16';

  if (componentType === CONTENT_TYPES.REVIEW_PANEL && pageType === 'contentPage')
    return 'px-4 lg:px-8 lg:max-w-1280 py-8 lg:py-16 mt-8 lg:mt-16';

  if (componentType === CONTENT_TYPES.EQUIPMENT_PANEL && pageType === 'contentPage')
    return 'px-4 lg:px-8 lg:max-w-1280 py-24 mt-8 lg:mt-16';

  if (componentType === CONTENT_TYPES.EQUIPMENT_PANEL && pageType === 'articlePage')
    return 'w-full px-4 max-w-800 lg:px-0 py-24';

  if (componentType === CONTENT_TYPES.USP_PANEL && pageType === 'contentPage')
    return 'px-4 lg:px-8 lg:max-w-1280 mt-8 lg:mt-16 py-16 sm:py-24';

  if (componentType === CONTENT_TYPES.CTA_PANEL && pageType === 'articlePage')
    return 'w-full px-4 lg:px-0';

  if (componentType === CONTENT_TYPES.CARD_PANEL && pageType === 'contentPage')
    return 'px-4 lg:px-8 lg:max-w-1280 mt-8 lg:mt-16';

  if (componentType === CONTENT_TYPES.CTA_PANEL) return 'mt-8 lg:mt-16 ';

  if (componentType === CONTENT_TYPES.RICH_TEXT_PANEL && pageType === 'contentPage')
    return 'w-full lg:max-w-1280 p-4 lg:p-8';

  if (
    (componentType === CONTENT_TYPES.WEB_COLUMN && pageType === 'contentPage') ||
    (componentType === CONTENT_TYPES.DOUBLE_WEB_COLUMN && pageType === 'contentPage')
  )
    return 'px-4 lg:px-8 py-32 lg:max-w-1280';

  if (componentType === CONTENT_TYPES.DOUBLE_WEB_COLUMN && pageType === 'homePage')
    return 'px-4 lg:px-8 lg:max-w-1280';

  if (index && index === 0 && pageType === 'contentPage') return 'px-4 lg:px-8 lg:max-w-1280';

  return container[pageType] || '';
};

export const makePanelOption = (
  { contentType, ...restOfData }: PanelData,
  pageType: string,
  index: number | null = null,
): {
  style: string;
  component: ComponentType | null;
  props: object;
} => {
  const option: {
    style: string;
    component: ComponentType | null;
    props: object;
  } = { style: '', component: null, props: {} };

  switch (contentType) {
    case CONTENT_TYPES.NEWSLETTER_SUBSCRIPTION_PANEL:
      option.component = NewsletterSubscription;
      option.props = {
        newsletterSubscriptionProps: {
          ...restOfData,
          title: restOfData?.callToAction,
          width: pageType === 'articlePage' ? false : restOfData?.width,
        },
      };
      option.style =
        container?.[pageType] === container?.articlePage ? container?.[pageType] : 'mt-8 lg:mt-16';
      break;
    case CONTENT_TYPES.HERO_BANNER:
      option.component = HeroBanner;
      option.props = {
        heroBanner: {
          ...restOfData,
          callToAction: restOfData?.heroBannerCallToAction,
          description: restOfData?.heroBannerDescription,
        },
      };
      option.style =
        '[&:has(+div_section[class*="lg:bg-gray-50"])]:lg:mb-[calc(var(--section-gap)*-1)]';
      break;
    case CONTENT_TYPES.RICH_TEXT_PANEL:
      option.component = RichTextPanel;
      option.props = {
        ...restOfData,
        desktopTextAlignment: restOfData?.richTextDesktopTextAlignment,
      };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.PICTURE_PANEL:
      option.component = PicturePanel;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.CTA_PANEL:
      option.component = CtaPanel;
      option.props = { ctaPanel: restOfData, isNarrow: pageType === 'articlePage' };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.EQUIPMENT_PANEL:
      option.component = EquipmentPanel;
      option.props = {
        equipmentPanel: restOfData,
        maxContainerWidth: pageType === 'articlePage' ? 800 : 1216,
      };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.EMBED_PANEL:
      option.component = EmbedPanel;
      option.props = { embedPanel: restOfData };
      option.style = container?.[pageType] || '';
      break;
    case CONTENT_TYPES.ACCORDION:
      option.component = AccordionPanel;
      option.props = { accordionPanel: restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.USP_PANEL:
      option.component = UspPanel;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.REVIEW_PANEL:
      option.component = ReviewPanel;
      option.props = {
        ...restOfData,
        reviewsCollectionItems: restOfData?.reviewsCollection?.items,
      };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.DOUBLE_WEB_COLUMN:
      option.component = DoubleWebColumn;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.WEB_COLUMN:
      option.component = WebColumn;
      option.props = { ...restOfData };
      break;
    case CONTENT_TYPES.CARD_PANEL:
      option.component = CardPanel;
      option.props = { ...restOfData, cardsCollection: restOfData?.cardsCollection?.items };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.PARTNER_PANEL:
      option.component = PartnerPanel;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.WORKOUT_PANEL:
      option.component = WorkoutPanel;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.EXERCISE_PANEL:
      option.component = ExercisePanel;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.REST_PANEL:
      option.component = RestPanel;
      option.props = { ...restOfData };
      option.style = getUniqueContainerStyles(pageType, contentType, index);
      break;
    case CONTENT_TYPES.HERO_BANNER_CAROUSEL:
      option.component = HeroBannerCarousel;
      option.props = {
        ...restOfData,
        callToAction: restOfData?.heroBannerCallToAction,
        description: restOfData?.heroBannerDescription,
        heroBanners: restOfData?.heroBanners?.items || [],
      };
      option.style = 'w-full';
      break;
    case CONTENT_TYPES.VARIATION_CONTAINER:
      option.component = VariationContainer;
      option.props = { variationContainer: restOfData, pageType, contentType, index };
      option.style = 'w-full';
      break;
    default:
      option.component = null;
      option.props = {};
  }
  return option;
};
